import {
  MAX_TAGS,
  VaultObjectUploadData,
} from "@/components/vault/vault-object-util";
import { MetadataTabContent } from "@/components/vault/viewer/stickers/tabs/MetadataTabContent";
import { TabSteps } from "@/components/vault/viewer/stickers/tabs/TabSteps";
import { VaultObjectViewerTitle } from "@/components/vault/viewer/stickers/title/VaultObjectViewerTitle";
import {
  LoaderData,
  VaultObjectViewerData,
} from "@/components/vault/viewer/VaultObjectViewerModal";
import { useScreenStore } from "@/stores/useScreenStore";
import useVaultStore from "@/stores/zustand/vault/useVaultStore";
import { AssetData } from "@/types/data-types";
import {
  LICENSE,
  License,
  LICENSE_LABEL,
  VAULT_OBJECT_TYPE,
} from "@/types/enums";
import HENGE from "@henge-inc/global-types";
import HUI from "@henge-inc/ui/v1_2";
import { ICON as ICON_v1_1 } from "@henge-inc/ui/v1_1";
import { AnimatePresence } from "motion/react";
import React, {
  RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import useGaiaStoreContainer from "@/engine/viewer/core/useGaiaStoreContainer";
import { VaultObjectInspector } from "@/components/inspector/VaultObjectInspector";
import { Box, Card, Text } from "@radix-ui/themes";
import { ShowWindowTabContent } from "@/components/vault/viewer/stickers/tabs/ShowWindowTabContent";

const licenseSelectOptions: HUI.SelectItemInterface<License>[] = Object.values(
  LICENSE,
).map((license) => ({
  value: license,
  label: LICENSE_LABEL[license],
}));

interface ViewerStickerProps {
  onCloseModal: () => void;
  logoRef: RefObject<HTMLDivElement>;
  titleRef: RefObject<HTMLDivElement>;
  panelRef: RefObject<HTMLDivElement>;
  actionButtonRef: RefObject<HTMLDivElement>;
  loaderData: LoaderData | null;
  canvasStatus: "idle" | "loading" | "render" | "error" | "hidden";
  vaultObjectViewerData: VaultObjectViewerData | null;
  asset:
    | { type: typeof VAULT_OBJECT_TYPE.OBJECT; data: AssetData }
    | { type: typeof VAULT_OBJECT_TYPE.OBJECT; data: VaultObjectUploadData }
    | null;
}

export const ViewerSticker = ({
  onCloseModal,
  logoRef,
  titleRef,
  panelRef,
  actionButtonRef,
  loaderData,
  canvasStatus,
  vaultObjectViewerData,
  asset,
}: ViewerStickerProps) => {
  const isMobileScreen = useScreenStore((state) => state.isMobileScreen);

  const currentPage = useVaultStore((state) => state.currentPage);
  const currentTab = useVaultStore((state) => state.currentTab);
  const uploadData = useVaultStore((state) => state.uploadData);

  const layers = useGaiaStoreContainer((state) => state.viewingHengeLayers);
  const selectedLayerPath = useGaiaStoreContainer(
    (state) => state.selectedLayerPath,
  );
  const selectedLayer = layers?.find(
    (layer) => layer.path === selectedLayerPath,
  );

  useEffect(() => {
    setNewNameInputErrorMessage("");

    if (currentPage === "uploader") {
      setNewName(uploadData?.name || loaderData?.name || "");
      if (!uploadData) return;

      setNewDownloadable(uploadData.downloadable);
      setNewLicense(
        licenseSelectOptions.find(
          (license) => license.value === uploadData.license,
        ) || { value: LICENSE.CC_BY, label: LICENSE_LABEL.CC_BY },
      );
    }
  }, [currentPage]);

  const [newName, setNewName] = useState("");
  const newNameInputRef = useRef<HTMLInputElement>(null);
  const [newNameInputErrorMessage, setNewNameInputErrorMessage] = useState("");
  useEffect(() => {
    if (!newName) return;

    setNewNameInputErrorMessage("");
  }, [newName]);
  const [newTagValueList, setNewTagValueList] = useState<string[]>([]);
  const newTagInputRef = useRef<HTMLInputElement>(null);
  const [inputTagValue, setInputTagValue] = useState("");
  const [newTagInputErrorMessage, setNewTagInputErrorMessage] = useState("");
  useEffect(() => {
    if (!inputTagValue) return;

    setNewTagInputErrorMessage("");
  }, [inputTagValue]);
  const tagListRef = useRef<HTMLDivElement>(null);

  const handleAddTag = useCallback(() => {
    const trimmedValue = inputTagValue.trim().replace(/\s/g, "");
    if (
      trimmedValue &&
      !newTagValueList.some((tag) => tag === trimmedValue) &&
      newTagValueList.length < MAX_TAGS
    ) {
      setInputTagValue("");
      setNewTagValueList((prevTags) => [...prevTags, trimmedValue]);
    }
  }, [inputTagValue, newTagValueList]);

  const handleRemoveTag = useCallback(() => {
    setNewTagValueList((prev) => {
      const copied = [...prev];
      copied.splice(copied.length - 1, 1);

      return copied;
    });
  }, [inputTagValue]);

  const [newDownloadable, setNewDownloadable] = useState(true);
  const [newLicense, setNewLicense] =
    useState<HUI.SelectItemInterface<License> | null>({
      value: LICENSE.CC_BY,
      label: LICENSE_LABEL.CC_BY,
    });

  return (
    <>
      {canvasStatus === "error" && (
        <Box className={"absolute top-6 left-1/2 -translate-x-1/2"}>
          <Card>
            <Text size={"2"} weight={"medium"}>
              3D 모델 로딩 실패
            </Text>
          </Card>
        </Box>
      )}

      {/** Title */}
      <div
        style={{
          bottom: 0,
          width:
            vaultObjectViewerData?.embed || isMobileScreen ? "100%" : "auto",
          maxWidth:
            vaultObjectViewerData?.embed || isMobileScreen ? "100vw" : "90vw",
          height:
            vaultObjectViewerData?.embed || isMobileScreen ? "100vh" : "90vh",
          aspectRatio:
            vaultObjectViewerData?.embed || isMobileScreen ? "auto" : "16 / 9",
        }}
        className="pointer-events-none absolute left-0 bottom-0 rounded-l-[20px]"
      >
        {!vaultObjectViewerData?.embed && (
          <div className="flex flex-col justify-end gap-1.5 w-full h-full bottom-[128px]">
            <VaultObjectViewerTitle
              newName={newName}
              setNewName={setNewName}
              newNameInputErrorMessage={newNameInputErrorMessage}
              newTagValueList={newTagValueList}
              inputTagValue={inputTagValue}
              setInputTagValue={setInputTagValue}
              newTagInputErrorMessage={newTagInputErrorMessage}
              setNewNameInputErrorMessage={setNewNameInputErrorMessage}
              handleAddTag={handleAddTag}
              handleRemoveTag={handleRemoveTag}
              titleRef={titleRef}
              newNameInputRef={newNameInputRef}
              tagListRef={tagListRef}
              newTagInputRef={newTagInputRef}
              vaultObjectViewerData={vaultObjectViewerData}
            />
          </div>
        )}
      </div>
      {/** Inspector */}
      <div className="pointer-events-auto absolute top-5 left-5 z-20">
        {currentTab === "show-window" && selectedLayer && (
          <VaultObjectInspector layerInfo={selectedLayer} />
        )}
      </div>
      {/** Panel */}
      <div
        ref={panelRef}
        style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          width: `100%`,
          padding: "12px",
        }}
        className={"pointer-events-none rounded-[20px] z-20"}
      >
        <div
          style={{
            opacity: isMobileScreen || vaultObjectViewerData?.embed ? 0 : 1,
            pointerEvents:
              isMobileScreen || vaultObjectViewerData?.embed ? "none" : "auto",
            transition: "opacity 0.3s",
            width: currentTab === "metadata" ? "calc(100% - 248px)" : "100%",
            // overflow: currentTab === "metadata" ? "scroll hidden" : "visible",
          }}
        >
          <AnimatePresence mode="wait">
            {currentTab === "metadata" && (
              <MetadataTabContent
                loaderData={loaderData}
                vaultObjectViewerData={vaultObjectViewerData}
                asset={asset}
                newLicense={newLicense}
                setNewLicense={setNewLicense}
              />
            )}

            {currentTab === "show-window" && <ShowWindowTabContent />}
          </AnimatePresence>
        </div>
        <TabSteps
          vaultObjectViewerData={vaultObjectViewerData}
          newName={newName}
          newNameInputRef={newNameInputRef}
          setNewNameInputErrorMessage={setNewNameInputErrorMessage}
          newTagValueList={newTagValueList}
          newTagInputRef={newTagInputRef}
          setNewTagInputErrorMessage={setNewTagInputErrorMessage}
          newDownloadable={newDownloadable}
          newLicense={newLicense}
          onCloseModal={onCloseModal}
        />
      </div>
      {/* Action Buttons */}
      {!vaultObjectViewerData?.embed && (
        <div
          ref={actionButtonRef}
          className="flex absolute z-20"
          style={{
            right: "20px",
            top: "20px",
            flexDirection: "row",
            gap: "4px",
          }}
        >
          <HUI.Button
            kind={HENGE.BUTTON_KIND.borderless}
            colour={HENGE.BUTTON_COLOUR.gray}
            size={HENGE.DETAILED_SIZE.small}
            onClick={() => {
              onCloseModal();
            }}
            icon={{
              icon: ICON_v1_1.X,
              kind: HENGE.ICON_KIND.regular,
              size: HENGE.DETAILED_SIZE.small,
            }}
          />
        </div>
      )}
    </>
  );
};
